import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Crown, Trophy, History, MapPin, Flame, ArrowRight } from 'lucide-react';
import api from '@/lib/api';
import ArenaxLayout from './ArenaxLayout';
import ArenaxRivalryBadgeStrip from './ArenaxRivalryBadgeStrip';
import ArenaxComebackCallout from './ArenaxComebackCallout';

/**
 * Phase 2J — Eternal League Page.
 *
 * Hyperlocal sports memory infrastructure. Renders:
 *  - Reigning champion banner
 *  - Dynasties list (multi-title squads)
 *  - Active split call-to-join
 *  - Splits archive (every completed tournament's champion)
 */
export default function ArenaxSeasonDetail() {
  const { seasonId } = useParams();
  const [season, setSeason] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/arenax/seasons/${seasonId}`)
      .then(r => setSeason(r.data))
      .catch(() => setSeason(null))
      .finally(() => setLoading(false));
  }, [seasonId]);

  if (loading) {
    return <ArenaxLayout><div className="max-w-5xl mx-auto px-4 py-12">
      <div className="h-72 rounded-2xl border border-slate-800 animate-pulse" />
    </div></ArenaxLayout>;
  }
  if (!season) {
    return <ArenaxLayout><div className="max-w-5xl mx-auto px-4 py-20 text-center">
      <h2 className="text-2xl font-black text-slate-100">Season not found.</h2>
    </div></ArenaxLayout>;
  }

  const completedSplits = (season.splits || []).filter(s => s.status === 'completed' && s.champion_squad_name);
  const upcomingOrLive = (season.splits || []).filter(s => s.status !== 'completed');

  return (
    <ArenaxLayout>
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8 lg:py-12" data-testid="arenax-season-detail">
        {/* Hero */}
        <div className="rounded-2xl border border-amber-500/15 p-6 mb-6"
          style={{ background: 'linear-gradient(160deg, rgba(245,158,11,0.07), var(--ax-card-bg-soft))' }}>
          <div className="flex items-center gap-2 mb-2">
            <History className="h-4 w-4 text-amber-300" />
            <span className="text-[10px] tracking-[0.3em] text-amber-300 font-bold uppercase">ETERNAL LEAGUE</span>
          </div>
          <h1 className="text-3xl sm:text-4xl font-black text-slate-100 mb-2"
            data-testid="arenax-season-name">{season.name}</h1>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-400 mb-3">
            <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" /> {season.locality ? `${season.locality}, ` : ''}{season.city}</span>
            <span className="inline-flex items-center gap-1"><Flame className="h-3 w-3" /> {season.sport}</span>
            <span className="inline-flex items-center gap-1"><Trophy className="h-3 w-3" /> {season.splits_count} splits</span>
          </div>
          {season.description && (
            <p className="text-sm text-slate-300 leading-relaxed">{season.description}</p>
          )}

          {season.reigning_champion && (
            <div className="mt-4 inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-amber-500/30 bg-amber-500/10"
              data-testid="arenax-season-reigning-champion">
              <Crown className="h-4 w-4 text-amber-300" />
              <div>
                <div className="text-[9px] uppercase tracking-wider text-amber-300/70 font-bold">Reigning Champions</div>
                <div className="text-sm font-bold text-slate-100">{season.reigning_champion}</div>
              </div>
            </div>
          )}

          {season.active_split && (
            <div className="mt-4">
              <Link to={`/arenax/tournaments/${season.active_split.id}`}
                data-testid="arenax-season-active-split-cta"
                className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-bold text-slate-950 bg-gradient-to-r from-amber-300 to-pink-400 hover:brightness-110 transition-all">
                Current split: {season.active_split.name}
                <ArrowRight className="h-3 w-3" />
              </Link>
            </div>
          )}
        </div>

        {/* Phase 2K PR 2 — Rivalry badges scoped to this Eternal League */}
        <div className="mb-6">
          <ArenaxRivalryBadgeStrip mode="season" seasonId={season.id}
            title={`Rivalries inside ${season.name}`} />
        </div>

        {/* Phase 2K PR 3 — Comebacks of the Splits (Eternal League legend strip) */}
        <div className="mb-6">
          <ArenaxComebackCallout seasonId={season.id} />
        </div>

        {/* Dynasties */}
        {season.dynasties && season.dynasties.length > 0 && (
          <div className="rounded-2xl border border-emerald-500/15 p-5 mb-6"
            data-testid="arenax-season-dynasties"
            style={{ background: 'linear-gradient(160deg, rgba(16,185,129,0.06), var(--ax-card-bg-soft))' }}>
            <h3 className="text-sm font-bold text-slate-100 mb-4 flex items-center gap-2">
              <Crown className="h-4 w-4 text-emerald-300" /> Dynasties
            </h3>
            <div className="space-y-2">
              {season.dynasties.map(d => (
                <div key={d.squad_id} className="flex items-center justify-between p-3 rounded-lg bg-slate-900/40 border border-slate-800"
                  data-testid={`arenax-dynasty-row-${d.squad_id}`}>
                  <div>
                    <div className="text-sm font-black text-slate-100">{d.squad_name}</div>
                    <div className="text-[10px] text-slate-500">
                      Champions: {d.title_years.map(y => y.year).join(' · ')}
                    </div>
                  </div>
                  <span className="px-2 py-1 rounded-md text-[10px] font-black uppercase tracking-wider text-emerald-200 bg-emerald-500/10 border border-emerald-500/30">
                    {d.titles}× champion
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Splits archive */}
        <div className="rounded-2xl border border-slate-800 p-5"
          data-testid="arenax-season-archive"
          style={{ background: 'linear-gradient(160deg, rgba(8,145,178,0.10), var(--ax-card-bg-soft))' }}>
          <h3 className="text-sm font-bold text-slate-100 mb-4">All Splits</h3>
          {season.splits.length === 0 ? (
            <p className="text-xs text-slate-500">No splits yet. Be the captain that starts the first.</p>
          ) : (
            <div className="space-y-2">
              {season.splits.map(s => (
                <Link key={s.id} to={`/arenax/tournaments/${s.id}`}
                  data-testid={`arenax-season-split-${s.id}`}
                  className="block p-3 rounded-lg bg-slate-900/40 border border-slate-800 hover:border-amber-500/30 transition-all">
                  <div className="flex items-center justify-between gap-2">
                    <div className="min-w-0">
                      <div className="text-sm font-bold text-slate-100 truncate">{s.season_label || s.name}</div>
                      <div className="text-[10px] text-slate-500 uppercase tracking-wider">
                        {s.format.replace('_', ' ')} · {s.starts_on || 'TBD'}{s.ends_on ? ` → ${s.ends_on}` : ''}
                      </div>
                    </div>
                    {s.champion_squad_name ? (
                      <div className="flex items-center gap-1 shrink-0">
                        <Crown className="h-3.5 w-3.5 text-amber-300" />
                        <span className="text-xs font-bold text-amber-200">{s.champion_squad_name}</span>
                      </div>
                    ) : (
                      <span className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">
                        {s.status === 'in_progress' ? '🔴 Live' : 'Pending'}
                      </span>
                    )}
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>
    </ArenaxLayout>
  );
}
